// Hook para manejar pagos parciales en OrderForm
import { useState, useCallback, useMemo, useEffect } from 'react';
import type { PagoParcial, OrderData } from '../types/orderFormTypes';
import { calculateTotals, getPagosParcialesMatchTarget, isPagosParcialesValid } from '../utils/orderFormUtils';
import { isClienteNetoActive } from '../utils/clienteNetoOrder';
import { normalizeOrderPaymentMethod, parcialRowRequiresTransactionId } from '../utils/orderPaymentTransactionRules';

interface UsePagosParcialesProps {
  formData: OrderData;
  productDetails: { [key: string]: any };
  costoEnvio: string;
  initialPagos?: PagoParcial[];
}

export const usePagosParciales = ({
  formData,
  productDetails,
  costoEnvio,
  initialPagos = []
}: UsePagosParcialesProps) => {
  const [pagosParciales, setPagosParciales] = useState<PagoParcial[]>(initialPagos);

  // Sincronizar solo al cargar el pedido; no pisar abonos nuevos agregados en edición
  useEffect(() => {
    setPagosParciales((prev) => {
      if (prev.length > initialPagos.length) {
        return prev
      }
      if (initialPagos.length === 0 && prev.length === 0) {
        return prev
      }
      return initialPagos
    })
  }, [initialPagos])

  const agregarPagoParcial = useCallback(() => {
    const nuevoPago: PagoParcial = {
      metodo_pago: '',
      valor: 0,
      id_transaccion: '',
      dcto: '',
      forma_pago: formData.extraDetails?.forma_pago || '6',
      plazo: 30,
      pago_verificado: 0,
      fecha_creacion: new Date().toISOString()
    };
    setPagosParciales(prev => [...prev, nuevoPago]);
  }, [formData.extraDetails?.forma_pago]);

  const eliminarPagoParcial = useCallback((index: number) => {
    setPagosParciales(prev => prev.filter((_, i) => i !== index));
  }, []);

  const actualizarPagoParcial = useCallback((index: number, campo: keyof PagoParcial, valor: string | number) => {
    setPagosParciales(prev => {
      const nuevosPagos = [...prev];
      nuevosPagos[index] = {
        ...nuevosPagos[index],
        [campo]: valor
      };
      
      const paymentMethodPrincipal = normalizeOrderPaymentMethod(formData.extraDetails?.payment_method)

      // Si la fila pasa a un medio que no exige id_transacción, vaciar para no enviar valor viejo
      if (
        campo === 'metodo_pago' &&
        !parcialRowRequiresTransactionId(valor, paymentMethodPrincipal)
      ) {
        nuevosPagos[index].id_transaccion = ''
      }

      if (campo === 'metodo_pago') {
        const nv = String(valor).trim().toLowerCase()
        if (nv === 'cod') {
          nuevosPagos[index].pago_verificado = 1
        } else {
          nuevosPagos[index].pago_verificado = 0
        }
      }
      
      return nuevosPagos;
    });
  }, [formData.extraDetails?.payment_method]);

  // Calcular valores para Pagos Parciales (memoizado para evitar recálculos innecesarios)
  const valoresPagosParciales = useMemo(() => {
    const paymentMethod = (formData.extraDetails?.payment_method || '').toString().trim().toLowerCase()
    const isPartOrPlanSepare = paymentMethod === 'part' || paymentMethod === 'plan_separe'

    if (!isPartOrPlanSepare) {
      return {
        totalPedidoPagosParciales: 0,
        sumaPagosParciales: 0,
        esValidoPagosParciales: true
      };
    }
    
    const totals = calculateTotals(
      formData.lineItems || [],
      productDetails,
      costoEnvio,
      isClienteNetoActive(formData.extraDetails)
    );
    const totalPedidoSinRedondear =
      parseFloat(totals.total) + parseFloat(totals.shipping || '0');
    // Redondear el total del pedido al entero más cercano
    const totalPedido = Math.round(totalPedidoSinRedondear);
    const totalObjetivo = paymentMethod === 'part'
      ? getPagosParcialesMatchTarget(totalPedido, formData.extraDetails)
      : totalPedido;
    const suma = pagosParciales.reduce((sum, pago) => sum + (pago.valor || 0), 0);
    // Redondear la suma de pagos parciales al entero más cercano
    const sumaRedondeada = Math.round(suma);
    const esValido = isPagosParcialesValid(formData, pagosParciales, productDetails, costoEnvio)
    
    return {
      totalPedidoPagosParciales: totalObjetivo,
      sumaPagosParciales: sumaRedondeada,
      esValidoPagosParciales: esValido
    };
  }, [
    formData.extraDetails?.payment_method,
    formData.extraDetails?.cliente_neto,
    formData.extraDetails?.retencion_fuente_activa,
    formData.extraDetails?.retencion_fuente,
    formData.lineItems,
    costoEnvio,
    pagosParciales,
    productDetails
  ]);

  const esValido = useCallback(() => {
    return isPagosParcialesValid(formData, pagosParciales, productDetails, costoEnvio);
  }, [formData, pagosParciales, productDetails, costoEnvio]);

  return {
    pagosParciales,
    setPagosParciales,
    agregarPagoParcial,
    eliminarPagoParcial,
    actualizarPagoParcial,
    valoresPagosParciales,
    esValido
  };
};

